/**
 * Schémas Zod — Guide Alternance (v11 — mai 2026).
 *
 * Page de contenu stratégique :
 *   - Gérée par super_admin (CRUD complet)
 *   - Visible par audience configurée (visibilityConfig)
 *   - Blocs de contenu typés (hero, text, tips, lba_offers, stats, faq, cta)
 */
import { z } from 'zod';
// ─── Blocs de contenu ─────────────────────────────────────────────────────
const blockId = z.string().min(1).max(50);
export const heroBlockSchema = z.object({
    id: blockId,
    type: z.literal('hero'),
    badge: z.string().max(60).optional(),
    title: z.string().min(1).max(200),
    subtitle: z.string().max(600).optional(),
});
export const textBlockSchema = z.object({
    id: blockId,
    type: z.literal('text'),
    title: z.string().max(200).optional(),
    content: z.string().min(1).max(5000),
});
export const tipItemSchema = z.object({
    icon: z.string().max(10), // emoji
    title: z.string().max(200),
    body: z.string().max(1000),
});
export const tipsBlockSchema = z.object({
    id: blockId,
    type: z.literal('tips'),
    title: z.string().max(200),
    layout: z.enum(['cards', 'numbered', 'bullets']).default('cards'),
    tips: z.array(tipItemSchema).min(1).max(12),
});
export const lbaOffersBlockSchema = z.object({
    id: blockId,
    type: z.literal('lba_offers'),
    title: z.string().max(200).default("Offres d'alternance disponibles"),
    romeCodes: z.array(z.string().max(10)).max(10).default([]),
    maxResults: z.number().int().min(1).max(20).default(6),
    showApplyButton: z.boolean().default(true),
});
export const statItemSchema = z.object({
    value: z.string().max(50),
    label: z.string().max(200),
    source: z.string().max(300).optional(),
});
export const statsBlockSchema = z.object({
    id: blockId,
    type: z.literal('stats'),
    title: z.string().max(200),
    metrics: z.array(statItemSchema).min(1).max(6),
});
export const faqItemSchema = z.object({
    question: z.string().min(1).max(300),
    answer: z.string().min(1).max(2000),
});
export const faqBlockSchema = z.object({
    id: blockId,
    type: z.literal('faq'),
    title: z.string().max(200),
    items: z.array(faqItemSchema).min(1).max(24),
});
export const ctaBlockSchema = z.object({
    id: blockId,
    type: z.literal('cta'),
    title: z.string().max(200),
    body: z.string().max(500).optional(),
    buttonLabel: z.string().max(100),
    buttonUrl: z.string().url().max(500),
    variant: z.enum(['primary', 'secondary', 'outline']).default('primary'),
});
export const blockSchema = z.discriminatedUnion('type', [
    heroBlockSchema,
    textBlockSchema,
    tipsBlockSchema,
    lbaOffersBlockSchema,
    statsBlockSchema,
    faqBlockSchema,
    ctaBlockSchema,
]);
// ─── Visibilité ───────────────────────────────────────────────────────────
/**
 * Règles de visibilité granulaires (actives quand mode = 'custom').
 *
 *   formationIds        — Liste blanche de formations (vide = toutes)
 *   establishmentIds    — Liste blanche d'établissements (vide = tous)
 *   levels              — Niveaux d'études (vide = tous)
 *   roles               — Rôles autorisés (vide = tous)
 *   excludeFormationIds — Cas stratégique : exclure les étudiants INSCRITS
 *                         dans ces formations (ex: formations disposant déjà
 *                         d'un accompagnement alternance propre).
 */
export const customRulesSchema = z.object({
    formationIds: z.array(z.string().uuid()).default([]),
    establishmentIds: z.array(z.string().uuid()).default([]),
    levels: z.array(z.enum(['L3', 'M1', 'M2'])).default([]),
    roles: z.array(z.enum(['student', 'responsable', 'company'])).default(['student']),
    excludeFormationIds: z.array(z.string().uuid()).default([]),
});
export const visibilityConfigSchema = z.object({
    /** super_admin_only : visible uniquement par super_admin (mode test) */
    mode: z.enum(['super_admin_only', 'all_students', 'custom']),
    customRules: customRulesSchema.optional(),
});
// ─── LBA config ───────────────────────────────────────────────────────────
export const lbaConfigSchema = z.object({
    /** Codes ROME par défaut pour le widget lba_offers */
    defaultRomeCodes: z.array(z.string().max(10)).max(20).default([
        'G1201', 'G1202', 'G1301', 'G1401', 'G1402', 'G1403', 'G1501', 'G1601', 'G1701',
    ]),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    radius: z.number().int().min(1).max(200).optional(),
});
// ─── CRUD ─────────────────────────────────────────────────────────────────
export const guideUpdateSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    subtitle: z.string().max(600).optional().nullable(),
    blocks: z.array(blockSchema).max(20).optional(),
    visibilityConfig: visibilityConfigSchema.optional(),
    isPublished: z.boolean().optional(),
    lbaConfig: lbaConfigSchema.optional(),
});
//# sourceMappingURL=alternanceGuide.js.map